"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { SocialIcon } from "ui/icons"
import { useLandingAnalytics } from "../analytics"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { handleAnchorScroll } from "../scrollToAnchor"

type Props = {
  data: any
}

// --- Styled Components ---

/* One single background for the whole footer from the primary color package.
   The footer has no separate inner surface, so it does not use the secondary
   (inner) package — that dropdown is hidden in the CMS for this block (ASH-1510). */
const Footer = styled.footer`
  background-color: var(--comp-bg);
  color: var(--comp-text);
`

const GreyZone = styled.div`
  padding: 40px 20px 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 30px 120px;
  }
`

/* logo first in JSX = right side in RTL, about text fills the middle */
const GreyInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 48px;
  }
`

const AboutText = styled.p`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  text-align: center;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;
    min-width: 0;
    text-align: start;
  }
`

const QuickLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  /* mobile stacking order: links above socials; desktop keeps JSX order (socials right) */
  order: -1;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
    order: 0;
  }
`

const QuickLink = styled.a`
  /* 14px on mobile, 16px desktop (ASH-1573) */
  font-size: 14px;
  font-weight: 500;
  color: var(--comp-text);
  text-decoration: none;
  /* touch-friendly hit area on mobile */
  padding: 4px 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
  }

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const SocialsRow = styled.div`
  display: flex;
  gap: 16px;
`

/* the SocialIcon SVGs draw their own circle, no extra ring needed */
const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  color: var(--comp-text);

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const WhiteZone = styled.div`
  padding: 24px 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 32px 120px;
  }
`

/* socials first in JSX = right side in RTL, credit ends up left */
const WhiteInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
    gap: 32px;
  }
`

const MintCredit = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--comp-text);
`

// --- Main Component ---

type SocialEntry = {
  type: "facebook" | "instagram" | "linkedin"
  url: string
  label: string
}

function LandingFooter({ data }: Props) {
  const analytics = useLandingAnalytics()
  const logo = extractProperty(data, "logo", true) as any[] | undefined
  const aboutText = extractProperty(data, "aboutText") as string | ""
  const quickLinks = extractProperty(data, "quickLinks") as any[] | ""

  /* JSX order renders right-to-left on the page: instagram ends up rightmost, linkedin leftmost per design */
  const allSocials: SocialEntry[] = [
    {
      type: "instagram",
      url: (extractProperty(data, "instagramLink") as string) || "",
      label: "Instagram",
    },
    {
      type: "facebook",
      url: (extractProperty(data, "facebookLink") as string) || "",
      label: "Facebook",
    },
    {
      type: "linkedin",
      url: (extractProperty(data, "linkedInLink") as string) || "",
      label: "LinkedIn",
    },
  ]
  const socials = allSocials.filter((social) => social.url)

  const links = (Array.isArray(quickLinks) ? quickLinks : []).filter(
    (link: any) => link?.url && link?.name,
  )
  const logoUrl = logo?.[0]?.url || ""

  return (
    <Footer>
      <GreyZone>
        <GreyInner>
          {logoUrl && (
            <Image
              src={getUmbracoMediaUrl(logoUrl)}
              width={91}
              height={56}
              alt={logo?.[0]?.altText || ""}
              style={{ objectFit: "contain", flexShrink: 0 }}
            />
          )}
          {aboutText && <AboutText>{aboutText}</AboutText>}
        </GreyInner>
      </GreyZone>
      <WhiteZone>
        <WhiteInner>
          {socials.length > 0 && (
            <SocialsRow>
              {socials.map((social) => (
                <SocialLink
                  key={social.type}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  onClick={() => analytics.socialClick(social.type)}
                >
                  <SocialIcon
                    type={social.type}
                    hover={false}
                    fill="currentColor"
                    width="45"
                    height="45"
                  />
                </SocialLink>
              ))}
            </SocialsRow>
          )}
          {links.length > 0 && (
            <QuickLinks>
              {links.map((link: any, i: number) => (
                <QuickLink
                  key={i}
                  href={getLinkHrefFromMultiUrlPickerItem(link)}
                  onClick={(e) => {
                    analytics.footerLinkClick(link.name)
                    handleAnchorScroll(e, getLinkHrefFromMultiUrlPickerItem(link))
                  }}
                  target={link.target || undefined}
                  rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                >
                  {link.name}
                </QuickLink>
              ))}
            </QuickLinks>
          )}
          {/* dir attribute: the emotion RTL plugin flips a CSS direction rule, the HTML attribute it cannot touch */}
          <MintCredit dir="ltr">
            Created by
            <Image width={69} height={21} alt="Mint" src="/icons/mint.svg" />
          </MintCredit>
        </WhiteInner>
      </WhiteZone>
    </Footer>
  )
}

export default LandingFooter
